import React, { useState, useEffect } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import Navbar from "../../components/layout/Navbar";
import Footer from "../../components/layout/Footer";
import HeroBanner from "../../components/layout/HeroBanner";
import {
  fetchTvStoreCategories,
  fetchAllContent,
  fetchMovieDetailsBatch,
  fetchGenres,
  fetchUserBanners,
  fetchMovieDetail,
  fetchTrailer,
  fetchAgeRatings,
  getImageUrl,
} from "../../redux/content";
import { buildContentIndex } from "../../utils/contentIndex";
import { getClientApiUrl } from "../../config/apiConfig";
import "../../components/styles/genreCategoryPage.scss";

/**
 * GenreCategoryPage — Genre/Rent category browse screen
 * Figma: 1.1 Category (Desktop) (24654:6404) — 1920×1240
 *
 * Data flow:
 *   1. Fetch TVOD products + VOD movies + genres
 *   2. Build content index (Map + inverted genre index)
 *   3. Filter products by genre from URL param
 *   4. Render poster grid of matching products
 *
 * Sections:
 *   1. Hero with genre title
 *   2. Filter tabs: All | Movie | TV Series
 *   3. Wrapping poster grid
 *   4. Footer
 */

const FILTER_TABS = ["All", "Movie", "TV Series"];

const GenreCategoryPage = () => {
  const { name, genre } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("All");
  const [contentIndex, setContentIndex] = useState(null);
  const [loading, setLoading] = useState(true);

  const tvStoreCategories = useSelector((state) => state.content.tvStoreCategories);
  const { moviesByCategories, categories } = useSelector((state) => state.content);
  const banners = useSelector((state) => state.content.banners);
  const bannersLoading = useSelector((state) => state.content.loaders.banners);
  const [heroSlides, setHeroSlides] = useState([]);
  const [slidesBuilding, setSlidesBuilding] = useState(true);

  const displayName = name || genre;
  const isAllRentals = !displayName; // /rent with no genre param
  const genreName = isAllRentals
    ? "Rentals"
    : decodeURIComponent(displayName)
        .replace(/-/g, " ")
        .replace(/\b\w/g, (c) => c.toUpperCase());

  // Fetch data on mount
  useEffect(() => {
    if (tvStoreCategories.length === 0) fetchTvStoreCategories();
    if (categories.length === 0) fetchAllContent();
    if (banners.length === 0) fetchUserBanners("DESKTOP");
  }, [tvStoreCategories.length, categories.length, banners.length]);

  // Build hero slides from VOD banners (same logic as Home/Discovery)
  const vodBanners = banners.filter((b) => b.type === "VOD");
  useEffect(() => {
    if (vodBanners.length === 0) return;

    const loadSlides = async () => {
      const [genreMap, ageRatingMap] = await Promise.all([fetchGenres(), fetchAgeRatings()]);

      const slides = await Promise.all(
        vodBanners.map(async (banner) => {
          const slide = {
            bgImage: banner.preview_image_id ? getImageUrl({ image_id: banner.preview_image_id }) : null,
            title: banner.title || null,
            description: banner.description || null,
            playHref: banner.content_id ? `/player/${banner.link || banner.content_id}?type=movie` : null,
            playState: banner.content_id ? { movieId: banner.content_id } : null,
            detailsHref: banner.content_id ? `/details/${banner.content_id}` : null,
            trailerUrl: null,
            rating: null,
            rated: null,
            year: null,
            genres: null,
          };

          if (banner.content_id) {
            try {
              const movie = await fetchMovieDetail(banner.content_id);
              if (movie) {
                slide.rating = movie.critics_rating ? String(movie.critics_rating) : null;
                slide.year = movie.year ? String(movie.year) : null;
                slide.rated = movie.age_rating_id ? ageRatingMap[movie.age_rating_id] || null : null;
                const genreIds = movie.movie_genres || movie.genres || [];
                slide.genres = genreIds.length > 0 ? genreIds.map((g) => genreMap[g.id || g] || "").filter(Boolean) : null;
                if (!slide.description || slide.description.length < 30) {
                  slide.description = movie.description || slide.description;
                }
                const titleArtStoreId = Array.isArray(movie.movie_image_stores)
                  ? movie.movie_image_stores.find((s) => s.type === "TITLE_ART")?.image_store_id
                  : null;
                const titleArtId = movie.title_art_image_id || titleArtStoreId;
                if (titleArtId) slide.titleArt = getImageUrl({ image_id: titleArtId });
                if (movie.cm_trailer || movie.trailer_url_ott || movie.has_trailer) {
                  const trailerUrl = await fetchTrailer(banner.content_id, "movie");
                  if (trailerUrl) slide.trailerUrl = trailerUrl;
                }
              }
            } catch (e) {
              console.warn("[GenreCatPage] Failed to load movie meta for banner:", banner.title, e.message);
            }
          }

          return slide;
        })
      );

      setHeroSlides(slides);
      setSlidesBuilding(false);
    };

    loadSlides();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vodBanners.length]);

  // Build content index when data is available
  useEffect(() => {
    const tvod = tvStoreCategories.find((c) => c.uid === "tvod");
    if (!tvod?.tv_store?.length) return;

    // Get VOD movies from Redux (from fetchAllContent)
    const allMovies = Object.values(moviesByCategories).flat();
    if (allMovies.length === 0) return;

    const buildIndex = async () => {
      setLoading(true);
      console.log("[GenreCatPage] Building content index...");

      // Fetch genre map
      const genreMap = await fetchGenres();

      // Fetch movie details for genre data (movie_genres not in VOD content response)
      const movieDetails = await fetchMovieDetailsBatch(allMovies);

      // Get TVOD product details from user tv store
      // We already have product stubs from categories — fetch user tv store for prices/images
      const accessToken = localStorage.getItem("mango_access_token");
      const userId = localStorage.getItem("mango_user_id");
      let tvodProducts = [];

      try {
        const res = await fetch(
          `${getClientApiUrl()}/api/client/v1/9mobile/users/${userId}/tv_store`,
          { headers: { Authorization: `Bearer ${accessToken}` } }
        );
        const data = await res.json();
        if (data.status === "ok") {
          const tvodIds = tvod.tv_store.map((p) => p.id);
          tvodProducts = data.data.filter((p) => tvodIds.includes(p.id));
        }
      } catch (e) {
        console.warn("[GenreCatPage] Failed to fetch user tv store:", e.message);
      }

      const index = buildContentIndex(movieDetails, tvodProducts, genreMap);
      setContentIndex(index);
      setLoading(false);

      console.log("[GenreCatPage] Index ready. Stats:", index.stats);
      console.log("[GenreCatPage] Available genres:", index.getAvailableGenres().map((g) => g.name).join(", "));
    };

    buildIndex();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tvStoreCategories.length, Object.keys(moviesByCategories).length]);

  // Filter products by genre — show all when on /rent with no genre
  const filteredProducts = contentIndex
    ? isAllRentals
      ? contentIndex.enrichedProducts
      : contentIndex.filterByGenreName(genreName)
    : [];

  // Further filter by tab
  const displayProducts = activeTab === "All"
    ? filteredProducts
    : activeTab === "Movie"
    ? filteredProducts.filter((p) => p.movieType === "movie")
    : filteredProducts.filter((p) => p.movieType === "series");

  console.log("[GenreCatPage] genre:", genreName, "filtered:", filteredProducts.length, "display:", displayProducts.length, "tab:", activeTab);

  return (
    <div className="genre-cat-page">
      <Navbar activeTab={location.pathname.startsWith("/rent") ? "Rent" : "Home"} />

      <HeroBanner
        loading={(bannersLoading || slidesBuilding) && heroSlides.length === 0}
        banners={heroSlides.length > 0 ? heroSlides : undefined}
        fromTab="Rent"
      />

      {/* Genre title + Filter Tabs */}
      <h1 className="genre-cat-page__section-title">{genreName}</h1>
      <div className="genre-cat-page__tabs">
        {FILTER_TABS.map((tab) => (
          <button
            key={tab}
            className={`genre-cat-page__tab ${activeTab === tab ? "genre-cat-page__tab--active" : ""}`}
            onClick={() => setActiveTab(tab)}
          >
            {tab}
          </button>
        ))}
      </div>

      {/* Poster Grid */}
      <section className="genre-cat-page__grid">
        {loading &&
          Array.from({ length: 12 }).map((_, i) => (
            <div key={`skel-${i}`} className="genre-cat-page__poster genre-cat-page__poster--skeleton">
              <div className="genre-cat-page__poster-shimmer" />
            </div>
          ))
        }
        {!loading && displayProducts.length === 0 && (
          <p className="genre-cat-page__loading">No {genreName} content available</p>
        )}
        {displayProducts.map((product, i) => {
          const posterUrl = product.card_image_id
            ? getImageUrl({ image_id: product.card_image_id })
            : product.movieImageId
            ? getImageUrl({ image_id: product.movieImageId })
            : null;

          return (
            <div
              key={product.id || i}
              className="genre-cat-page__poster"
              onClick={() => navigate(`/details/${product.movieId || product.uid}`, {
                state: {
                  rentalPrice: product.price_options?.[0]?.activation_price ?? null,
                  tvStoreProductId: product.id,
                  // action is an array per API: ["ACTIVATE"] = unpurchased, ["DEACTIVATE"] = active rental.
                  // price_options[].purchased is a date (string) when purchased, null otherwise.
                  rentalPurchased:
                    (Array.isArray(product.action) && product.action.includes("DEACTIVATE")) ||
                    !!product.price_options?.some((po) => po.purchased),
                  fromRent: true,
                },
              })}
              role="button"
              tabIndex={0}
              title={product.movieTitle || product.name}
              style={{ cursor: "pointer" }}
            >
              {posterUrl ? (
                <img src={posterUrl} alt={product.movieTitle || product.name || ""} className="genre-cat-page__poster-img" />
              ) : (
                <div className="genre-cat-page__poster-placeholder">
                  <span>{product.name || product.uid}</span>
                </div>
              )}
            </div>
          );
        })}
      </section>

      <Footer />
    </div>
  );
};

export default GenreCategoryPage;
